import React, { useEffect, useState, useCallback } from "react";
import api from "../../../lib/api";
import { Loader2, Package, Truck, Warehouse, ClipboardList, Info } from "lucide-react";
import PackageVerificationCard from "./PackageVerificationCard";

// CP2.5 — Read-only staff view of the parcel-shipping payload attached
// to a paid retail order. Rendered inside OrderDetailsDrawer when the
// order has fulfillment_mode === "shipping". No writes. No new
// calculations. No purchase-label / requote / verify / tracking / void
// actions — those land in CP3/CP4.
//
// Contract:
//   GET /api/retail/orders/{order_id}/shipping
//   404 → order has no shipping payload (drawer silently omits the panel).
//   200 → { order_id, order_code, currency, fulfillment_mode, shipping,
//           timeline, ledger, pricing_explanation, readonly_placeholders }

function fmtMoney(v, currency) {
  if (v == null) return "—";
  const sym = { USD: "$", INR: "₹", EUR: "€", GBP: "£" }[(currency || "").toUpperCase()] || "";
  return sym ? `${sym}${Number(v).toFixed(2)}` : `${Number(v).toFixed(2)} ${currency || ""}`.trim();
}

function fmtDim(v, unit) {
  return v == null ? "—" : `${v}${unit ? ` ${unit}` : ""}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return String(iso); }
}

const TIMELINE_LABEL = {
  "shipping.quote.created":       "Quote created",
  "shipping.quote.selected":      "Quote selected",
  "shipping.payment.completed":   "Payment completed",
  "shipping.awaiting_fulfillment": "Awaiting fulfillment",
};

const LEDGER_LABEL = {
  customer_charge:    "Customer charge",
  shipping_discount:  "Shipping discount",
  merchant_subsidy:   "Merchant subsidy",
  carrier_cost:       "Carrier cost",
};

const LEDGER_DIRECTION_COLOR = {
  revenue: "text-emerald-700 bg-emerald-50 border-emerald-200",
  expense: "text-rose-700 bg-rose-50 border-rose-200",
};

export default function OrderShippingPanel({ orderId }) {
  const [state, setState] = useState({ loading: true, error: null, data: null });

  const load = useCallback(() => {
    setState({ loading: true, error: null, data: null });
    api.get(`/retail/orders/${orderId}/shipping`)
      .then((r) => setState({ loading: false, error: null, data: r.data }))
      .catch((e) => {
        if (e?.response?.status === 404) {
          setState({ loading: false, error: "not_a_shipping_order", data: null });
        } else {
          setState({ loading: false, error: "load_failed", data: null });
        }
      });
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  if (state.loading) {
    return (
      <div className="py-4 grid place-items-center text-slate-400" data-testid="shipping-panel-loading">
        <Loader2 className="w-4 h-4 animate-spin" />
      </div>
    );
  }
  if (state.error === "not_a_shipping_order") return null;
  if (state.error) {
    return (
      <div className="rounded-md bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2" data-testid="shipping-panel-error">
        Could not load shipping details.
      </div>
    );
  }
  if (!state.data) return null;

  const d = state.data;
  const s = d.shipping || {};
  const fin = s.financial_summary || {};
  const currency = d.currency || "USD";
  const addr = s.shipping_address || {};

  return (
    <section data-testid="order-shipping-panel" className="space-y-4">
      <div className="flex items-center gap-2">
        <Truck className="w-4 h-4 text-indigo-600" />
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
          Parcel Shipping
        </div>
        <span className="ml-auto inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-700">
          PARCEL
        </span>
      </div>

      {/* Address */}
      <div className="rounded-md border border-slate-200 p-3 bg-slate-50/60" data-testid="shipping-panel-address">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Ship to</div>
        <div className="text-sm text-slate-900 whitespace-pre-line">
          {addr.name || "—"}
          {addr.street1 ? `\n${addr.street1}` : ""}
          {addr.street2 ? `\n${addr.street2}` : ""}
          {addr.city || addr.region || addr.postal ? `\n${[addr.city, addr.region, addr.postal].filter(Boolean).join(", ")}` : ""}
          {addr.country ? `\n${addr.country}` : ""}
        </div>
      </div>

      {/* Carrier / service / speed / warehouse */}
      <div className="grid grid-cols-2 gap-3" data-testid="shipping-panel-carrier">
        <Field label="Carrier" value={s.carrier_label || "—"} />
        <Field label="Service code" value={s.service_code || "—"} mono />
        <Field label="Speed band" value={s.speed_band_label || "—"} />
        <Field label="Carrier account" value={s.carrier_account_label || "—"} />
        <Field label={<span className="inline-flex items-center gap-1"><Warehouse className="w-3 h-3" />Warehouse</span>} value={s.warehouse_label || "—"} />
        <Field label="Quote ID" value={s.selected_quote_id || "—"} mono />
      </div>

      {/* Package rows */}
      <div data-testid="shipping-panel-packages">
        <SectionTitle icon={<Package className="w-3.5 h-3.5" />}>Estimated packages</SectionTitle>
        <ul className="mt-2 divide-y divide-slate-100 border border-slate-200 rounded-md">
          {(s.packages || []).length === 0 && (
            <li className="px-3 py-2 text-xs text-slate-500">No packages recorded.</li>
          )}
          {(s.packages || []).map((p, idx) => (
            <li key={p.package_id || idx} className="px-3 py-2 text-sm" data-testid={`shipping-panel-package-${idx}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium">{p.package_type_name || p.package_type_id || `Package ${idx + 1}`}</div>
                  <div className="text-xs text-slate-500">
                    {fmtDim(p.estimated?.length, p.estimated?.unit)} × {fmtDim(p.estimated?.width, p.estimated?.unit)} × {fmtDim(p.estimated?.height, p.estimated?.unit)}
                    {" · "}
                    {fmtDim(p.estimated?.weight, p.estimated?.weight_unit)}
                  </div>
                </div>
                <div className="text-right text-[10px] uppercase text-slate-400 whitespace-nowrap space-y-0.5">
                  <div>Verified: <em className="not-italic text-slate-500">— (CP3)</em></div>
                  <div>Label: <em className="not-italic text-slate-500">— (CP4)</em></div>
                  <div>Tracking: <em className="not-italic text-slate-500">— (CP5)</em></div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Financial summary */}
      <div data-testid="shipping-panel-financials">
        <SectionTitle>Shipping financials</SectionTitle>
        <div className="mt-2 rounded-md border border-slate-200 p-3 space-y-1.5">
          <MoneyRow label="Quoted carrier amount" value={fin.quoted_carrier_amount} currency={currency} testid="shipping-fin-quoted" />
          <MoneyRow label="Customer shipping charge" value={fin.customer_shipping_charge} currency={currency} testid="shipping-fin-customer" bold />
          <MoneyRow label="Shipping discount" value={fin.shipping_discount} currency={currency} testid="shipping-fin-discount" muted />
          <MoneyRow label="Free-shipping subsidy" value={fin.free_shipping_subsidy} currency={currency} testid="shipping-fin-subsidy" muted />
          <MoneyRow label="Merchant subsidy total" value={fin.merchant_subsidy_total} currency={currency} testid="shipping-fin-merchant" muted />
        </div>
      </div>

      {/* Pricing explanation — read-only interpretation. */}
      {d.pricing_explanation?.human_readable_lines?.length > 0 && (
        <div data-testid="shipping-panel-pricing-explanation" className="rounded-md border border-indigo-100 bg-indigo-50/50 p-3">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-indigo-700 font-semibold">
            <Info className="w-3 h-3" /> Pricing explanation
          </div>
          <ul className="mt-1.5 text-xs text-slate-700 space-y-0.5 list-disc pl-4">
            {d.pricing_explanation.human_readable_lines.map((ln, i) => (
              <li key={i}>{ln}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Inventory */}
      <div className="grid grid-cols-2 gap-3" data-testid="shipping-panel-inventory">
        <Field label="Inventory status" value={s.inventory_status_label || "—"} />
        <Field label="Reservation ID" value={s.inventory_reservation_id || "—"} mono />
      </div>

      {/* Timeline */}
      <div data-testid="shipping-panel-timeline">
        <SectionTitle icon={<ClipboardList className="w-3.5 h-3.5" />}>Shipment timeline</SectionTitle>
        <ol className="mt-2 border border-slate-200 rounded-md divide-y divide-slate-100">
          {(d.timeline || []).length === 0 && (
            <li className="px-3 py-2 text-xs text-slate-500">No timeline events yet.</li>
          )}
          {(d.timeline || []).map((t) => (
            <li key={t.id || `${t.event_type}-${t.sequence}`} className="px-3 py-2 flex items-start justify-between gap-3 text-sm" data-testid={`shipping-timeline-row-${t.sequence}`}>
              <div className="min-w-0">
                <div className="font-medium">
                  <span className="text-[10px] inline-block w-5 text-slate-400 font-mono">{t.sequence}.</span>
                  {" "}{TIMELINE_LABEL[t.event_type] || t.event_type}
                </div>
                <div className="text-[10px] text-slate-400 truncate">actor: {t.actor?.kind || "system"}</div>
              </div>
              <div className="text-[10px] text-slate-400 whitespace-nowrap">
                {fmtDate(t.occurred_at)}
              </div>
            </li>
          ))}
        </ol>
      </div>

      {/* Ledger (display-only) */}
      {(d.ledger || []).length > 0 && (
        <div data-testid="shipping-panel-ledger">
          <SectionTitle>Financial ledger</SectionTitle>
          <ul className="mt-2 border border-slate-200 rounded-md divide-y divide-slate-100">
            {d.ledger.map((row) => (
              <li key={row.id} className="px-3 py-2 flex items-center justify-between gap-3 text-sm" data-testid={`shipping-ledger-row-${row.entry_type}`}>
                <div className="min-w-0">
                  <div className="font-medium">{LEDGER_LABEL[row.entry_type] || row.entry_type}</div>
                  <div className="text-[10px] text-slate-400 truncate">{row.reason || "—"}</div>
                </div>
                <div className={`px-2 py-0.5 rounded-full border text-xs ${LEDGER_DIRECTION_COLOR[row.direction] || "text-slate-600 bg-slate-50 border-slate-200"}`}>
                  {fmtMoney(row.amount, row.currency || currency)}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* CP3 — Package verification workflow (only surfaces once the
          order is paid & shipping). */}
      <PackageVerificationCard
        orderId={d.order_id}
        verification={d.verification}
        checkoutEstimate={(s.packages || [])[0]?.estimated}
        onChanged={load}
      />

      {/* FedEx Parcel Shipping Completion (Phase 5) — this block used to
          say "Purchase label / Verify package / Void label · coming in
          CP3/CP4" even though `PackageVerificationCard` right above
          already provides all three, live. Only continuous carrier
          tracking-event polling (not the one-time tracking number shown
          once a label is purchased) remains genuinely deferred — see
          `readonly_placeholders` from `routes/retail_shipping_panel.py`. */}
      <div className="rounded-md border border-dashed border-slate-200 bg-slate-50/40 p-3 text-xs text-slate-500" data-testid="shipping-panel-placeholders">
        <div className="uppercase text-[10px] tracking-wider text-slate-400 font-semibold mb-1">Not yet available</div>
        <div>Live carrier tracking-event updates · <em>deferred (tracking number is shown once a label is purchased; ongoing status polling is not)</em></div>
      </div>
    </section>
  );
}

function SectionTitle({ children, icon }) {
  return (
    <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold inline-flex items-center gap-1.5">
      {icon}{children}
    </div>
  );
}

function Field({ label, value, mono = false }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-sm text-slate-900 ${mono ? "font-mono text-xs break-all" : ""}`}>{value}</div>
    </div>
  );
}

function MoneyRow({ label, value, currency, testid, bold = false, muted = false }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className={`${muted ? "text-slate-500" : "text-slate-600"}`}>{label}</span>
      <span
        data-testid={testid}
        className={`${bold ? "font-bold text-slate-900" : (muted ? "text-slate-600" : "text-slate-800")}`}
      >
        {fmtMoney(value, currency)}
      </span>
    </div>
  );
}
